import { Component, OnDestroy, OnInit, Input, ViewChild, ElementRef } from '@angular/core';
import { AccountService } from 'src/app/services/account.service';
import { AlertService } from 'src/app/services/alert.service';
import { KilnService } from 'src/app/services/kiln.service';
import { UserService } from 'src/app/services/user.service';
import { UntypedFormControl, UntypedFormGroup, UntypedFormArray, Validators, FormControl } from '@angular/forms';
import { ToastController, AlertController, Platform } from '@ionic/angular';
import { PushNotificationService } from '../services/push-notification.service';
import { ToggleService } from '../services/toggle.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-alert-settings-popup',
  templateUrl: './alert-settings-popup.page.html',
  styleUrls: ['./alert-settings-popup.page.scss'],
})
export class AlertSettingsPopupPage implements OnInit, OnDestroy {
  @Input() onDismiss;
  @ViewChild('kilnSelectRef', { static: false, read: ElementRef }) kilnSelectRef: ElementRef<HTMLElement>;

  public selectedKiln = null;
  public selectedAlert = null;
  public kilns = [];
  public alertSettings = [];
  public selectedAlertSettings;
  public alerts = [
    {name: "Kiln back online", type: 'back-online'},
    {name: "Firing has started", type: 'firing-started'},
    {name: "Successful firing", type: 'successful-firing'},
    {name: "Failed firing", type: 'failed-firing'},
    {name: "Kiln has heated to [n] degrees", type: 'heated-to'},
    {name: "Kiln has cooled to [n] degrees", type: 'cooled-to'},
    {name: "Ready to unload", type: 'unload-ready'},
    {name: "Receiving bad data", type: 'bad-data'},
    {name: "MySkutt network device offline", type: 'network-device-offline'},
    {name: "No messages received from kiln", type: 'no-messages'},
    {name: "Network device back online", type: 'network-device-online'}
  ];

  public alertSettingsForm: UntypedFormGroup;
  public gettingUsers;
  public loadingView = false;
  public viewMode = true;
  public showCopyToPanel = false;
  public copyTargetKilnIds: string[] = [];
  public bulkOperationInProgress = false;
  public viewTestAlertType: string = null;
  private arrayItems = [];
  public savingSettings;
  private accountInformation;
  public canAdminister;
  private loginData;
  private kiln_id;
  /** Whether push notifications feature is enabled (pushNotifications toggle). */
  public showPushNotifications = false;

  /** When embedded in Account Admin on a small layout, parent toolbar already shows the section title. */
  private layoutChromeSubs = new Subscription();

  constructor(public accountService: AccountService,
              public alertService: AlertService,
              public userService: UserService,
              public kilnService: KilnService,
              public toastController: ToastController,
              public alertController: AlertController,
              public router: Router,
              private route: ActivatedRoute,
              private pushNotificationService: PushNotificationService,
              private toggleService: ToggleService,
              private translate: TranslateService,
              private platform: Platform
            ) {
    let login_data = this.userService.getLoginData();
    this.loginData = (login_data.hasOwnProperty('loggedInAs') && login_data.loggedInAs) ? login_data.loggedInAs : login_data;
    // this.loginData = userService.getLoginData();
    accountService.getAccountInformation().then( (data) => {
      this.accountInformation = data;
      this.setUpCanAdminister(data);
    });

    this.route.params.subscribe(params => {
      this.kiln_id = params["kiln_id"];
    })
  }

  ngOnDestroy() {
    this.layoutChromeSubs.unsubscribe();
  }

  ngOnInit() {   
    this.toggleService.listToggles();
    this.toggleService.togglesLoaded.subscribe(() => {
      this.showPushNotifications = this.toggleService.isToggleEnabled('pushNotifications');
    });
    if (this.toggleService.areTogglesLoaded()) {
      this.showPushNotifications = this.toggleService.isToggleEnabled('pushNotifications');
    }
    const alertEnabled = new UntypedFormControl(false, [Validators.required]);
    const enabled_users = new UntypedFormArray([]);
    const selectedAlertTypes = new UntypedFormArray(
      this.alerts.map((_, i) => new UntypedFormGroup({ selected: new UntypedFormControl(i === 0) }))
    );
    this.alertSettingsForm = new UntypedFormGroup({
      selectedKiln: new FormControl(),
      selectedAlert: new FormControl('back-online'),
      selectedAlertTypes,
      alertEnabled: alertEnabled,
      enabled_users: enabled_users,
      temp_variable: new UntypedFormControl(null)
    });
    this.savingSettings = false;

    this.accountService.getDevicesForAccount()
      .then((data) => {
        this.kilns = (data.kilns || []).sort((a, b) => (a.name > b.name) ? 1 : -1);
        if (this.kilns.length === 0) return;
        const id = this.kiln_id || this.kilns[0].serial_number || `${this.kilns[0].mac_address}${this.kilns[0].kiln_id}`;
        this.selectedKiln = this.kilns.find((k) => (k.serial_number === id) || (`${k.network_device_id}${k.kiln_id}`) === id) || this.kilns[0];
        if (!this.selectedKiln && this.kilns.length > 0) {
          this.selectedKiln = this.kilns[0];
        }
        this.alertSettingsForm.get('selectedKiln').setValue(this.selectedKiln);
        if (this.viewMode) {
          this.loadAlertSettingsForView();
        } else {
          this.selectKiln(this.getKilnId(this.selectedKiln), this.selectedKiln.mac_address, this.selectedKiln.kiln_id);
        }
      });
  }

  getKilnId(kiln) {
    if (!kiln) return null;
    return kiln.serial_number || `${kiln.mac_address}${kiln.kiln_id}`;
  }

  compareKilns = (k1: unknown, k2: unknown): boolean => {
    if (!k1 || !k2) return k1 === k2;
    return this.getKilnId(k1) === this.getKilnId(k2);
  };

  loadAlertSettingsForView() {
    if (!this.selectedKiln) return;
    this.loadingView = true;
    const id = this.getKilnId(this.selectedKiln);
    this.alertService.getAlertSettingsForKiln(id)
      .then((data) => {
        this.alertSettings = data || [];
        this.loadingView = false;
      })
      .catch(() => { this.loadingView = false; });
  }

  onViewKilnChange(event) {
    const kiln = event.detail && event.detail.value;
    if (kiln) {
      this.selectedKiln = kiln;
      this.loadAlertSettingsForView();
    }
  }

  openKilnSelect() {
    if (this.kilns && this.kilns.length > 0 && this.kilnSelectRef?.nativeElement) {
      this.kilnSelectRef.nativeElement.click();
    }
  }

  startEditing() {
    this.viewMode = false;
    if (this.selectedKiln && !this.selectedKiln.users) {
      this.selectKiln(this.getKilnId(this.selectedKiln), this.selectedKiln.mac_address, this.selectedKiln.kiln_id);
    } else if (this.selectedKiln && this.selectedKiln.users && this.alertSettingsForm) {
      this.alertSettingsForm.get('selectedKiln').setValue(this.selectedKiln);
      this.alertSettingsForm.get('selectedAlert').setValue(this.selectedAlert ? this.selectedAlert.type : 'back-online');
      this.selectAlert();
    }
  }

  cancelEditing() {
    this.viewMode = true;
    this.loadAlertSettingsForView();
  }

  getAlertName(type) {
    const a = this.alerts.find((x) => x.type === type);
    return a ? a.name : type;
  }

  getSettingForType(type) {
    return (this.alertSettings || []).find((s) => s.type === type) || this.defaultAlertSettings(type);
  }

  getRecipientsList(setting) {
    if (!setting) return [];
    const emails = (setting.enabled_emails || []);
    const phones = (setting.enabled_phones || []);
    const pushDevices = (setting as any).push_devices || [];
    const push = pushDevices
      .filter((d: { enabled_for_alert?: boolean }) => d.enabled_for_alert)
      .map((d: { name?: string }) => `Push to ${d.name || 'device'}`);
    return [...emails, ...phones, ...push];
  }

  /** Recipients list for display; excludes push when pushNotifications toggle is off. */
  getRecipientsListForDisplay(setting) {
    const list = this.getRecipientsList(setting);
    if (this.showPushNotifications) return list;
    return list.filter((r: string) => !r.startsWith('Push to '));
  }

  getRecipientCounts(setting) {
    if (!setting) return { emails: 0, phones: 0, push: 0 };
    const pushDevices = (setting as any).push_devices || [];
    const pushCount = pushDevices.filter((d: { enabled_for_alert?: boolean }) => d.enabled_for_alert).length;
    return {
      emails: (setting.enabled_emails || []).length,
      phones: (setting.enabled_phones || []).length,
      push: pushCount
    };
  }

  /** Push devices for the current alert type (edit mode). Empty if none. */
  getPushDevices(): { id: number; name: string; enabled_for_alert: boolean }[] {
    const setting = this.selectedAlertSettings;
    if (!setting || !(setting as any).push_devices) return [];
    return (setting as any).push_devices;
  }

  /** Whether any user has push devices (edit mode; devices are shown per user). */
  showPushDevicesSection(): boolean {
    return this.showPushNotifications && this.getPushDevices().length > 0;
  }

  expandedRecipientsType: string = null;
  toggleRecipientsDetails(type: string) {
    this.expandedRecipientsType = this.expandedRecipientsType === type ? null : type;
  }

  /** Selected alert types as array of type strings (for small-screen multi-select). */
  getSelectedAlertTypesForSelect(): string[] {
    if (!this.alertSettingsForm) return [];
    const arr = this.alertSettingsForm.get('selectedAlertTypes') as UntypedFormArray;
    if (!arr || !arr.length) return [];
    return this.alerts
      .map((a, index) => ({ type: a.type, selected: !!(arr.at(index) as UntypedFormGroup)?.get('selected')?.value }))
      .filter((x) => x.selected)
      .map((x) => x.type);
  }

  /** Sync FormArray checkboxes from small-screen multi-select change event. */
  onAlertTypesMultiChange(event: any) {
    const selectedTypes: string[] = event?.detail?.value || [];
    const arr = this.alertSettingsForm.get('selectedAlertTypes') as UntypedFormArray;
    if (!arr) return;
    this.alerts.forEach((a, index) => {
      const group = arr.at(index) as UntypedFormGroup;
      const control = group && (group.get('selected') as UntypedFormControl);
      if (control) {
        control.setValue(selectedTypes.includes(a.type));
      }
    });
    this.onAlertTypeToggled();
  }

  /** Selected channels (email/phones) for a user index, used by small-screen multi-select. */
  getUserChannelsForSelect(userIndex: number): string[] {
    const result: string[] = [];
    const usersArr = this.enabledUsers();
    const userGroup = usersArr && (usersArr.at(userIndex) as UntypedFormGroup);
    if (!userGroup) return result;

    if (userGroup.get('email_checked')?.value) {
      result.push('email');
    }
    const phonesArr = userGroup.get('phones') as UntypedFormArray;
    if (phonesArr && phonesArr.length > 0) {
      phonesArr.controls.forEach((ctrl, idx) => {
        if ((ctrl as UntypedFormGroup).get('checked')?.value) {
          result.push(`phone-${idx}`);
        }
      });
    }
    return result;
  }

  /** Handle small-screen user channel multi-select changes. */
  onUserChannelsMultiChange(userIndex: number, event: any) {
    const selected: string[] = event?.detail?.value || [];
    const usersArr = this.enabledUsers();
    const userGroup = usersArr && (usersArr.at(userIndex) as UntypedFormGroup);
    if (!userGroup) return;

    // Email
    const emailCtrl = userGroup.get('email_checked') as UntypedFormControl;
    if (emailCtrl) {
      emailCtrl.setValue(selected.includes('email'));
    }

    // Phones
    const phonesArr = userGroup.get('phones') as UntypedFormArray;
    if (phonesArr && phonesArr.length > 0) {
      phonesArr.controls.forEach((ctrl, idx) => {
        const phoneGroup = ctrl as UntypedFormGroup;
        const checkedCtrl = phoneGroup.get('checked') as UntypedFormControl;
        if (checkedCtrl) {
          checkedCtrl.setValue(selected.includes(`phone-${idx}`));
        }
      });
    }

    // Reuse existing handler to keep any additional logic consistent
    this.checkboxChange({ detail: { checked: true } }, userIndex);
  }

  getOtherKilns() {
    if (!this.selectedKiln || !this.kilns.length) return [];
    const currentId = this.getKilnId(this.selectedKiln);
    return this.kilns.filter((k) => this.getKilnId(k) !== currentId);
  }

  isCopyTarget(kiln) {
    const id = this.getKilnId(kiln);
    return this.copyTargetKilnIds.indexOf(id) >= 0;
  }

  toggleCopyTarget(kiln) {
    const id = this.getKilnId(kiln);
    const idx = this.copyTargetKilnIds.indexOf(id);
    if (idx >= 0) {
      this.copyTargetKilnIds = this.copyTargetKilnIds.filter((x) => x !== id);
    } else {
      this.copyTargetKilnIds = [...this.copyTargetKilnIds, id];
    }
  }

  async clearAllAlerts() {
    if (!this.selectedKiln || !this.canAdminister || this.bulkOperationInProgress) return;
    const kilnName = this.selectedKiln.name || 'this kiln';
    const alert = await this.alertController.create({
      header: 'Clear all alerts',
      message: `Disable all alert types for ${kilnName}? No notifications will be sent until you re-enable them.`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        { text: 'Clear all', role: 'destructive', handler: () => this.doClearAllAlerts() }
      ]
    });
    await alert.present();
  }

  private doClearAllAlerts() {
    const kilnId = this.getKilnId(this.selectedKiln);
    if (!kilnId) return;
    this.bulkOperationInProgress = true;
    const types = this.alerts.map((a) => a.type);
    let done = 0;
    const onDone = () => {
      done++;
      if (done === types.length) {
        this.bulkOperationInProgress = false;
        this.loadAlertSettingsForView();
        this.showMessage('All alerts cleared for this kiln.', true);
      }
    };
    types.forEach((type) => {
      const data = { enable: false, enabled_users: [], disabled_users: [], type };
      this.alertService.updateAlertSettings(kilnId, data)
        .then(() => onDone())
        .catch(() => { onDone(); this.showMessage('Some alerts could not be cleared.', false); });
    });
  }

  openCopyToPanel() {
    this.showCopyToPanel = true;
    this.copyTargetKilnIds = [];
  }

  cancelCopyTo() {
    this.showCopyToPanel = false;
    this.copyTargetKilnIds = [];
  }

  async copySettingsToSelectedKilns() {
    if (!this.selectedKiln || !this.canAdminister || this.bulkOperationInProgress || this.copyTargetKilnIds.length === 0) return;
    const settings = this.alertSettings || [];
    if (settings.length === 0) {
      this.showMessage('No settings to copy. Edit this kiln first to configure alerts.', false);
      return;
    }

    const targetKilns = this.kilns.filter((k) => this.copyTargetKilnIds.indexOf(this.getKilnId(k)) >= 0);

    // If any selected target kiln already has alert settings configured, warn that settings will be overwritten.
    try {
      const kilnsWithExistingSettings: any[] = [];
      for (const target of targetKilns) {
        const targetId = this.getKilnId(target);
        if (!targetId) continue;
        try {
          const existing = await this.alertService.getAlertSettingsForKiln(targetId);
          if (Array.isArray(existing) && existing.some((s: any) =>
            s &&
            (
              (Array.isArray(s.enabled_emails) && s.enabled_emails.length > 0) ||
              (Array.isArray(s.enabled_phones) && s.enabled_phones.length > 0) ||
              (s as any).enabled_push ||
              s.enabled
            )
          )) {
            kilnsWithExistingSettings.push(target);
          }
        } catch {
          // Ignore errors when checking existing settings; treat as no configured alerts.
        }
      }

      if (kilnsWithExistingSettings.length > 0) {
        const header =
          this.translate?.instant?.('ALERT_SETTINGS.COPY_OVERWRITE_TITLE') ||
          'Overwrite existing alert settings?';
        const kilnNames = kilnsWithExistingSettings
          .map((k) => k.name)
          .filter((n) => !!n)
          .join(', ');
        const i18nMessage = this.translate?.instant?.('ALERT_SETTINGS.COPY_OVERWRITE_MESSAGE');
        const defaultMessage = kilnNames
          ? `The following kilns already have alert settings configured: ${kilnNames}. Copying settings will replace their current alert settings. Do you want to continue?`
          : 'One or more selected kilns already have alert settings configured. Copying settings will replace their current alert settings. Do you want to continue?';
        const message = i18nMessage || defaultMessage;
        const continueLabel = this.translate?.instant?.('COMMON.CONTINUE') || 'Continue';
        const cancelLabel = this.translate?.instant?.('ALERT_SETTINGS.CANCEL') || 'Cancel';
        const alert = await this.alertController.create({
          header,
          message,
          buttons: [
            { text: cancelLabel, role: 'cancel' },
            { text: continueLabel, role: 'confirm' }
          ]
        });
        await alert.present();
        const result = await alert.onDidDismiss();
        if (result.role !== 'confirm') {
          return;
        }
      }
    } catch {
      // If confirmation fails for any reason, fall through and perform the copy without the extra prompt.
    }

    this.bulkOperationInProgress = true;
    let completed = 0;
    const total = targetKilns.length * settings.length;
    const onOneDone = () => {
      completed++;
      if (completed >= total) {
        this.bulkOperationInProgress = false;
        this.showCopyToPanel = false;
        this.copyTargetKilnIds = [];
        this.showMessage('Alert settings copied to selected kiln(s).', true);
      }
    };
    const payloadFor = (setting) => {
      const enabled = (setting.enabled_emails || []).concat(setting.enabled_phones || []);
      const data = { enable: setting.enabled, enabled_users: enabled, disabled_users: [], type: setting.type };
      if (setting.temp_variable != null && ['heated-to', 'cooled-to'].includes(setting.type)) {
        data['temp_variable'] = setting.temp_variable;
      }
      return data;
    };
    targetKilns.forEach((target) => {
      const targetId = this.getKilnId(target);
      settings.forEach((setting) => {
        this.alertService.updateAlertSettings(targetId, payloadFor(setting))
          .then(() => onOneDone())
          .catch(() => { onOneDone(); this.showMessage('Copy failed for some settings.', false); });
      });
    });
  }

  async showMessage(message, success) {
    this.savingSettings = false;
    let toast = await this.toastController.create({
      message: message,
      duration: 3000,
      position: 'middle'
    });

    toast.present();
  }

  checkboxChange(val, index) {
    const checked = val.detail ? val.detail.checked : (val.target && (val.target.checked || val.target.value === 'on'));
    const user = this.enabledUsers().controls[index];
    if (checked) {
      this.arrayItems.push(user);
    } else {
      this.arrayItems.pop();
    }
  }

  enabledUsers() {
    let formArr = (this.alertSettingsForm.get("enabled_users") as UntypedFormArray);
    return formArr;
  }

  userHasEmail(index) {
    let userControls = (this.enabledUsers().value[index] as UntypedFormGroup);
    return userControls && userControls["email_label"];
  }

  emailCanAlert(index) {
    let userControls = (this.enabledUsers().value[index] as UntypedFormGroup);
    return userControls && userControls["can_be_checked"];
  }

  userPhoneControls(index) {
    let phoneFormArray = (this.enabledUsers().controls[index].get("phones") as UntypedFormArray);
    return phoneFormArray.controls;
  }

  userPhoneControlsAt(index) {
    let phoneFormArray = (this.enabledUsers().controls[index].get("phones") as UntypedFormArray);
    return phoneFormArray.controls[index];
  }

  userPushDeviceControls(userIndex: number): UntypedFormGroup[] {
    const pushDevicesArr = this.enabledUsers().controls[userIndex]?.get('push_devices') as UntypedFormArray;
    return pushDevicesArr?.controls as UntypedFormGroup[] || [];
  }

  getCurrentUserPushLabel(): string {
    const name = this.pushNotificationService.getCurrentDeviceName();
    if (name && name.trim()) {
      return (this.translate?.instant?.('ALERT_SETTINGS.PUSH_TO_DEVICE') || 'Push to {{name}}').replace('{{name}}', name.trim());
    }
    return this.translate?.instant?.('ALERT_SETTINGS.PUSH_THIS_DEVICE') || 'Push (this device)';
  }

  isCurrentDeviceUser(userIndex: number): boolean {
    const user = this.enabledUsers().at(userIndex);
    if (!user || !this.loginData) return false;
    const emailLabel = user.get('email_label')?.value;
    if (!emailLabel) return false;
    const email = (emailLabel as string).replace(/\*$/, '');
    return email === this.loginData.email || email === (this.loginData as any).mail_to;
  }

  async onPushToggle(userIndex: number, event: { detail?: { value?: boolean; checked?: boolean } }) {
    const checked = event?.detail?.value ?? event?.detail?.checked ?? false;
    if (!checked) return;
    // Only prompt and register this device when the current user enables push; other users just get the checkbox toggled
    if (!this.isCurrentDeviceUser(userIndex)) return;
    const header = this.translate?.instant?.('ALERT_SETTINGS.PUSH_SETUP_TITLE') || 'Push notifications';
    const message = this.translate?.instant?.('ALERT_SETTINGS.PUSH_SETUP_MESSAGE') || 'Set up push notifications on this device to receive alerts?';
    const ok = this.translate?.instant?.('COMMON.YES') || 'Yes';
    const cancel = this.translate?.instant?.('ALERT_SETTINGS.CANCEL') || 'Cancel';
    return new Promise<void>((resolve) => {
      this.alertController.create({ header, message, buttons: [
        { text: cancel, role: 'cancel', handler: () => { this.enabledUsers().at(userIndex).get('push_checked').setValue(false); resolve(); } },
        { text: ok, handler: async () => {
          if (!this.pushNotificationService.isPushAvailable) {
            this.showMessage(this.translate?.instant?.('ALERT_SETTINGS.PUSH_NOT_AVAILABLE') || 'Push notifications are not available on this device.', false);
            this.enabledUsers().at(userIndex).get('push_checked').setValue(false);
            resolve();
            return;
          }
          try {
            const result = await this.pushNotificationService.requestPermissionAndRegister();
            if (result?.token) {
              const deviceName = this.pushNotificationService.getCurrentDeviceName();
              await this.alertService.registerPushToken(result.token, deviceName || undefined);
              this.showMessage(this.translate?.instant?.('ALERT_SETTINGS.PUSH_REGISTERED') || 'Push notifications enabled for this device.', true);
            } else {
              this.enabledUsers().at(userIndex).get('push_checked').setValue(false);
              this.showMessage(this.translate?.instant?.('ALERT_SETTINGS.PUSH_REGISTER_FAILED') || 'Could not register for push notifications.', false);
            }
          } catch {
            this.enabledUsers().at(userIndex).get('push_checked').setValue(false);
            this.showMessage(this.translate?.instant?.('ALERT_SETTINGS.PUSH_REGISTER_FAILED') || 'Could not register for push notifications.', false);
          }
          resolve();
        } }
      ] }).then((alert) => alert.present());
    });
  }

  async showPopupIfFormUnsaved(callback) {
    callback();   
  }

  goBack() {
    this.router.navigate(["/landing"]);
  }

  selectKilnFromDropdown(event) {
    let kiln = event.detail && event.detail.value ? event.detail.value : event;
    this.selectedKiln = kiln;
    let id = this.kiln_id || this.selectedKiln.serial_number || `${this.selectedKiln.mac_address}${this.selectedKiln.kiln_id}`;            
    this.selectKiln(id, kiln.mac_address, kiln.kiln_id);
  }

  selectKiln(id, kiln_id, mac_address) {
    // kiln = this.alertSettingsForm.value.selectedKiln;
    let actuallySelect = () => {      
      this.kilnService.getUsersForKiln(id, mac_address, kiln_id)
          .then((data) => {
            this.gettingUsers = false;
            if(this.selectedKiln) {
              this.selectedKiln.users = data;
              this.alertSettingsForm.get('selectedAlert').setValue('back-online');
              this.selectAlert();
            }
          })
          .catch(() => { this.gettingUsers = false; })

      this.gettingUsers = true;
      this.alertSettings = [];
      this.alertService.getAlertSettingsForKiln(id)
          .then((data) => {
            this.alertSettings = data;
            this.alertSettingsForm.get('selectedAlert').setValue('back-online');
            this.selectAlert();
          })
    }

    // if(this.alertSettingsForm.dirty) {
    //   this.showPopupIfFormUnsaved(actuallySelect);
    // } else {
      actuallySelect();
    // }
  }

  async saveAlertSettings(formValue) {
    if (this.savingSettings) {
      this.showMessage("An update to this alert's settings is already underway. Please wait till it is complete.", false);
      return;
    }
    const types = this.getSelectedAlertTypes();
    if (types.length === 0) return;

    let users = formValue.enabled_users && formValue.enabled_users.reduce((acc, user) => {
      if (user.email_label) {
        const email_lbl = user.email_label.replace("*", '');
        if (user.email_checked) acc.enabled.push(email_lbl);
        else acc.disabled.push(email_lbl);
      }
      if (user.phones && user.phones.length > 0) {
        user.phones.forEach((p) => {
          if (p.label) {
            const label = p.label.replace("*", '');
            if (p.checked) acc.enabled.push(label);
            else acc.disabled.push(label);
          }
        });
      }
      return acc;
    }, { enabled: [], disabled: [] });

    const currentDeviceName = this.pushNotificationService.getCurrentDeviceName()?.trim() || '';
    const pushEnabledForCurrentUser = (formValue.enabled_users || []).some((u: any) => {
      const email = (u.email_label as string)?.replace(/\*$/, '').trim();
      if (email !== this.loginData?.email && email !== (this.loginData as any)?.mail_to) return false;
      return (u.push_devices || []).some((d: any) => d.enabled && d.name && (d.name as string).trim() === currentDeviceName);
    });
    const enabledPushUserEmails: string[] = (formValue.enabled_users || [])
      .filter((u: any) => (u.push_devices || []).some((d: any) => d.enabled))
      .map((u: any) => (u.email_label as string)?.replace(/\*$/, '').trim().toLowerCase())
      .filter((e: string | undefined): e is string => !!e);

    if (pushEnabledForCurrentUser) {
      let token = this.pushNotificationService.getStoredToken();
      if (!token && this.pushNotificationService.isPushAvailable) {
        const result = await this.pushNotificationService.requestPermissionAndRegister();
        token = result?.token || null;
      }
      if (token) {
        try {
          const deviceName = this.pushNotificationService.getCurrentDeviceName();
          await this.alertService.registerPushToken(token, deviceName || undefined);
        } catch {
          this.showMessage(this.translate?.instant?.('ALERT_SETTINGS.PUSH_REGISTER_FAILED') || 'Could not register push token.', false);
        }
      }
    }

    const kilnId = this.selectedKiln.serial_number || `${this.selectedKiln.mac_address}${this.selectedKiln.kiln_id}`;
    this.savingSettings = true;
    let completed = 0;
    const total = types.length;
    const onDone = () => {
      completed++;
      if (completed >= total) {
        this.savingSettings = false;
        this.alertSettingsForm.markAsPristine();
        this.alertSettingsForm.markAsUntouched();
        this.showMessage(types.length > 1 ? "Alert settings were updated successfully." : "Alert setting was updated successfully.", true);
        this.viewMode = true;
        this.loadAlertSettingsForView();
      }
    };

    const pushDeviceIds: number[] = (formValue.enabled_users || [])
      .flatMap((u: any) => (u.push_devices || []).filter((d: any) => d.enabled).map((d: any) => d.id));

    types.forEach((type) => {
      const data: any = {
        enable: true,
        enabled_users: users.enabled,
        disabled_users: users.disabled,
        type
      };
      data.push_token_ids = pushDeviceIds;
      if (pushEnabledForCurrentUser) {
        data.push_enabled = true;
      }
      if (enabledPushUserEmails.length > 0) {
        data.enabled_push_users = enabledPushUserEmails;
      }
      if (formValue.temp_variable != null && formValue.temp_variable !== '' && ['heated-to', 'cooled-to'].includes(type)) {
        data.temp_variable = formValue.temp_variable;
      }
      if (this.canAdminister && this.selectedKiln.serial_number) {
        this.alertService.updateAlertSettings(kilnId, data)
          .then((response) => { this.alertSettings = response; onDone(); })
          .catch(() => { this.showMessage("Alert setting was not updated successfully.", false); onDone(); });
      } else {
        this.alertService.updateSingleUserAlertSettings(kilnId, data)
          .then((response) => {
            const idx = this.alertSettings.findIndex((s) => s.type === response.type);
            if (idx >= 0) this.alertSettings.splice(idx, 1, response);
            else this.alertSettings.push(response);
            onDone();
          })
          .catch(() => { this.showMessage("Alert setting was not updated successfully.", false); onDone(); });
      }
    });
  }

  getSelectedAlertTypes(): string[] {
    const arr = this.alertSettingsForm.get('selectedAlertTypes') as UntypedFormArray;
    if (!arr || !arr.controls) return [];
    return this.alerts
      .filter((_, i) => arr.at(i)?.get('selected')?.value)
      .map((a) => a.type);
  }

  editFormValid(): boolean {
    const types = this.getSelectedAlertTypes();
    if (types.length === 0) return false;
    const needsTemp = types.some((t) => ['heated-to', 'cooled-to'].includes(t));
    const tempVal = this.alertSettingsForm.get('temp_variable')?.value;
    if (needsTemp && (tempVal == null || tempVal === '')) return false;
    return true;
  }

  onAlertTypeToggled() {
    // Keep selectedAlert in sync for any logic that still uses it (e.g. temp_variable)
    const types = this.getSelectedAlertTypes();
    if (types.length) {
      this.alertSettingsForm.get('selectedAlert').setValue(types[0]);
      this.selectedAlert = this.alerts.find((a) => a.type === types[0]);
    }
  }

  showTempVariable() {
    return this.getSelectedAlertTypes().some((t) => ['heated-to', 'cooled-to'].includes(t));
  }

  tempVariableDisplayValue(): string | number {
    const v = this.alertSettingsForm.get('temp_variable')?.value;
    return v != null && v !== '' ? v : '—';
  }

  selectAlert() {
    let type = this.alertSettingsForm.value.selectedAlert;
    let actuallySelectAlert = () => {
      this.selectedAlert = this.alerts.find((a) => { return a.type === type; });
      this.selectedAlertSettings = this.alertSettings.find((setting) => {
        return setting.type === type;
      }) || this.defaultAlertSettings(type);
      let alertEnabled = new UntypedFormControl(this.selectedAlertSettings.enabled, [Validators.required]);
      let all_enabled = this.selectedAlertSettings.enabled_phones.concat(this.selectedAlertSettings.enabled_emails);
      let enabled = (val) => {
        return all_enabled.includes(val);
      }

      if(this.canAdminister) {
        let enabled_users_groups = [];
        if(this.selectedKiln && this.selectedKiln.users) {
          // should add email, but also phone options
          let createPhoneArray = (u) => {
            if(u && u.phones && u.phones.length > 0 && u.phones[0] !== null) {
              return new UntypedFormArray(u.phones.map((p) => {
                let enabled_and_verified = (p.enabled && p.verified);
                return new UntypedFormGroup({
                  checked: new UntypedFormControl(enabled(p.call_number)),
                  label: new UntypedFormControl(`${p.call_number}${enabled_and_verified ? '' : '*'}`),
                  can_be_checked: new UntypedFormControl(enabled_and_verified),
                })
              }))
            } else {
              return new UntypedFormArray([])
            }
          }

          const pushTokenIdsForAlert: number[] = (this.selectedAlertSettings as any)?.push_token_ids || [];
          const allPushDevices = (this.selectedKiln?.users || []).flatMap((u: any) =>
            (u.push_tokens || []).map((pt: { id: number; name?: string }) => ({
              id: pt.id,
              name: pt.name || '',
              user_id: u.id,
              enabled_for_alert: Array.isArray(pushTokenIdsForAlert) && pushTokenIdsForAlert.indexOf(pt.id) >= 0,
            }))
          );
          const createPushDevicesArray = (u: { id?: number }) => {
            const userDevices = allPushDevices.filter(
              (d: { user_id?: number }) => d.user_id != null && u.id != null && d.user_id === u.id
            );
            return new UntypedFormArray(
              userDevices.map((d: { id: number; name: string; enabled_for_alert: boolean }) =>
                new UntypedFormGroup({
                  id: new UntypedFormControl(d.id),
                  name: new UntypedFormControl(d.name || ''),
                  enabled: new UntypedFormControl(!!d.enabled_for_alert),
                })
              )
            );
          };

          const enabledPushUsers: string[] = (this.selectedAlertSettings as any)?.enabled_push_users || [];
          enabled_users_groups = this.selectedKiln.users.map((u) => {
            let enabled_and_verified = (u.email_enabled && u.email_verified);
            let has_first_last_name = u.first_name || u.last_name;
            const isCurrentUser = this.loginData && (u.email === this.loginData.email || u.email === (this.loginData as any).mail_to);
            const pushEnabled = isCurrentUser
              ? !!(this.selectedAlertSettings as any)?.enabled_push
              : (u.email && enabledPushUsers.includes(u.email.toLowerCase()));
            return new UntypedFormGroup({
              username: new UntypedFormControl(u.email || (has_first_last_name ? `${u.first_name} ${u.last_name}` : u.username)),
              email_checked: new UntypedFormControl(u.email ? enabled(u.email) : false),
              email_label: u.email ? new UntypedFormControl(`${u.email}${enabled_and_verified ? '': '*'}`) : new UntypedFormControl(null),
              can_be_checked: u.email ? new UntypedFormControl(enabled_and_verified) : new UntypedFormControl(false),
              phones: createPhoneArray(u),
              push_devices: createPushDevicesArray(u),
              push_checked: new UntypedFormControl(pushEnabled),
              push_can_show: new UntypedFormControl(true)
            });
          })
        }

        let enabled_users = new UntypedFormArray(enabled_users_groups);

        let tempVariableControl = () => {
          if(this.showTempVariable()) {
            return new UntypedFormControl(this.selectedAlertSettings ? this.selectedAlertSettings.temp_variable : "", [Validators.required])
          } else { return new UntypedFormControl(null) }
        }

        this.alertSettingsForm = new UntypedFormGroup({
          selectedKiln: this.alertSettingsForm.controls.selectedKiln,
          selectedAlert: this.alertSettingsForm.controls.selectedAlert,
          selectedAlertTypes: this.alertSettingsForm.get('selectedAlertTypes') || new UntypedFormArray(this.alerts.map((_, i) => new UntypedFormGroup({ selected: new UntypedFormControl(i === 0) }))),
          alertEnabled: alertEnabled,
          enabled_users: enabled_users,
          temp_variable: tempVariableControl()
        });
      } else {
        let enabled_users_groups = [];
        if(this.selectedKiln && this.selectedKiln.users) {
          // should add email, but also phone options
          let createPhoneArray = (u) => {
            if(u && u.phones && u.phones.length > 0 && u.phones[0] !== null) {
              return new UntypedFormArray(u.phones.map((p) => {
                let enabled_and_verified = (p.enabled && p.verified);
                return new UntypedFormGroup({
                  checked: new UntypedFormControl(enabled(p.call_number)),
                  label: new UntypedFormControl(`${p.call_number}${enabled_and_verified ? '' : '*'}`),
                  can_be_checked: new UntypedFormControl(enabled_and_verified),
                })
              }))
            } else {
              return new UntypedFormArray([])
            }
          }
          const pushTokenIdsForAlertNonAdmin: number[] = (this.selectedAlertSettings as any)?.push_token_ids || [];
          const allPushDevicesNonAdmin = (this.selectedKiln?.users || []).flatMap((u: any) =>
            (u.push_tokens || []).map((pt: { id: number; name?: string }) => ({
              id: pt.id,
              name: pt.name || '',
              user_id: u.id,
              enabled_for_alert: Array.isArray(pushTokenIdsForAlertNonAdmin) && pushTokenIdsForAlertNonAdmin.indexOf(pt.id) >= 0,
            }))
          );
          const createPushDevicesArrayNonAdmin = (u: { id?: number }) => {
            const userDevices = allPushDevicesNonAdmin.filter(
              (d: { user_id?: number }) => d.user_id != null && u.id != null && d.user_id === u.id
            );
            return new UntypedFormArray(
              userDevices.map((d: { id: number; name: string; enabled_for_alert: boolean }) =>
                new UntypedFormGroup({
                  id: new UntypedFormControl(d.id),
                  name: new UntypedFormControl(d.name || ''),
                  enabled: new UntypedFormControl(!!d.enabled_for_alert),
                })
              )
            );
          };
          enabled_users_groups = this.selectedKiln.users.filter((u) => u.email === this.loginData.email).map((u) => {
            let enabled_and_verified = (u.email_enabled && u.email_verified);
            const pushEnabled = (this.selectedAlertSettings as any)?.enabled_push;
            return new UntypedFormGroup({
              username: new UntypedFormControl(u.username),
              email_checked: new UntypedFormControl(enabled(u.email)),
              email_label: u.email ? new UntypedFormControl(`${u.email}${enabled_and_verified ? '': '*'}`) : new UntypedFormControl(null),
              can_be_checked: new UntypedFormControl(enabled_and_verified),
              phones: createPhoneArray(u),
              push_devices: createPushDevicesArrayNonAdmin(u),
              push_checked: new UntypedFormControl(pushEnabled || false),
              push_can_show: new UntypedFormControl(true)
            });
          })
        }

        let enabled_users = new UntypedFormArray(enabled_users_groups);

        this.alertSettingsForm = new UntypedFormGroup({
          selectedKiln: this.alertSettingsForm.controls.selectedKiln,
          selectedAlert: this.alertSettingsForm.controls.selectedAlert,
          selectedAlertTypes: this.alertSettingsForm.get('selectedAlertTypes') || new UntypedFormArray(this.alerts.map((_, i) => new UntypedFormGroup({ selected: new UntypedFormControl(i === 0) }))),
          alertEnabled: alertEnabled,
          enabled_users: enabled_users,
          temp_variable: new UntypedFormControl(this.selectedAlertSettings ? this.selectedAlertSettings.temp_variable : "")
        });
      }
    }

    if(this.alertSettingsForm.dirty) {
      this.showPopupIfFormUnsaved(actuallySelectAlert);
    } else {
      actuallySelectAlert();
    }
  }

  defaultAlertSettings(type) {
    return {
      type: type,
      enabled_emails: [],
      enabled_phones: [],
      enabled_push: false,
      enabled_push_users: [],
      enabled: false,
      temp_variable: null
    }
  }

  setUpCanAdminister(data) {
    if(this.accountInformation && this.accountInformation.users && this.loginData) {
      let loggedInUserPermissions = this.accountInformation.users.find((u) => {
        return u.email === this.loginData.email
      })
      this.canAdminister = loggedInUserPermissions ? (loggedInUserPermissions.type === 'admin') : false;
    }
  }

  testAlert(mac, serial, type) {
    if (!type) return;
    this.alertService.sendTestAlert(mac, serial, type)
      .then((response) => this.showMessage(response?.message || 'Test alert sent.', true))
      .catch(() => this.showMessage('The test message could not be sent.', false));
  }
}
